import { useEffect, useState } from "react";
import { ColorCatalog } from "./components/ColorCatalog";
import { SwatchScanner } from "./components/SwatchScanner";
import { WallVisualizer } from "./components/WallVisualizer";
import { SixtyThirtyTenPanel } from "./components/SixtyThirtyTenPanel";
import { fetchColorCatalog } from "./lib/supabase-client";
import type { ColorRecord } from "./lib/color-engine";

const FAMILY_NAMES: Record<string, string> = {
  "FAM-01": "Pure Whites & Luminous Tints",
  "FAM-02": "Warm Greiges & Architectural Taupes",
  "FAM-03": "Earth, Clay & Terracotta Neutrals",
  "FAM-04": "Muted Botanical & Atmospheric Tones",
  "FAM-05": "Obsidian & High-Contrast Accents",
};

type Tab = "catalog" | "scanner" | "visualizer";

export default function App() {
  const [catalog, setCatalog] = useState<ColorRecord[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedColor, setSelectedColor] = useState<ColorRecord | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("catalog");

  useEffect(() => {
    let cancelled = false;
    fetchColorCatalog()
      .then((colors) => {
        if (cancelled) return;
        setCatalog(colors);
        setLoadState("ready");
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setErrorMessage(err.message);
        setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = (hex: string, shadeCode: string) => {
    const match = catalog.find((c) => c.shadeCode === shadeCode);
    if (match) {
      setSelectedColor(match);
      return;
    }
    // From the scanner/visualizer a tap gives a raw hex without a
    // guaranteed catalog id yet — represent it as a lightweight
    // catalog-shaped record so downstream components keep working.
    setSelectedColor({
      id: `adhoc-${hex}`,
      shadeCode,
      name: shadeCode,
      familyId: "",
      hex,
      lab: { l: 0, a: 0, b: 0 },
    });
  };

  return (
    <div className="min-h-screen bg-[#EDEBE6] text-[#232220]">
      <header className="border-b border-[#D9D5CC] bg-[#F7F6F3] px-4 py-4 sm:px-6">
        <h1 className="text-lg font-semibold">NEORISE</h1>
        <p className="text-sm text-[#6B6660]">Minimalist Color Universe & Visualizer</p>
      </header>

      <nav
        className="flex gap-2 overflow-x-auto border-b border-[#D9D5CC] bg-[#F7F6F3] px-4 py-2 sm:px-6"
        role="tablist"
      >
        {(["catalog", "scanner", "visualizer"] as Tab[]).map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={[
              "min-h-[44px] shrink-0 rounded-full px-4 text-sm font-medium capitalize",
              activeTab === tab
                ? "bg-[#52606D] text-white"
                : "text-[#232220] hover:bg-[#EDEBE6]",
            ].join(" ")}
          >
            {tab === "catalog" ? "Catalog" : tab === "scanner" ? "Swatch scanner" : "Room visualizer"}
          </button>
        ))}
      </nav>

      <main className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row">
        <div className="flex-1">
          {loadState === "loading" && (
            <p className="text-sm text-[#6B6660]">Loading color catalog…</p>
          )}
          {loadState === "error" && (
            <p className="text-sm text-red-700">
              Couldn't load the color catalog: {errorMessage}
            </p>
          )}

          {loadState === "ready" && activeTab === "catalog" && (
            <ColorCatalog
              colors={catalog}
              familyNames={FAMILY_NAMES}
              onSelect={handleSelect}
              selectedShadeCode={selectedColor?.shadeCode}
            />
          )}

          {activeTab === "scanner" && <SwatchScanner />}

          {activeTab === "visualizer" && (
            <WallVisualizer selectedHex={selectedColor?.hex ?? null} />
          )}
        </div>

        <aside className="w-full shrink-0 lg:w-80">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[#6B6660]">
            60:30:10 Palette
          </h2>
          <SixtyThirtyTenPanel baseColor={selectedColor} catalog={catalog} />
        </aside>
      </main>
    </div>
  );
}
